import { Box, IconButton, Tooltip, Grid, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Card, CardContent } from '@mui/material';
import {
    People as PeopleIcon,
    ChildCare as ChildCareIcon,
    CheckCircle as CheckCircleIcon,
    MenuBook as MenuBookIcon,
    CloudUpload as CloudUploadIcon,
    Link as LinkIcon,
    PersonAdd as PersonAddIcon,
    Group as GroupIcon,
    Analytics as AnalyticsIcon,
    Sync as SyncIcon,
    History as HistoryIcon,
    Settings as SettingsIcon,
    Email as EmailIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { DateTime } from 'luxon';
import api from '../../services/api';

const Todd = () => {
    const navigate = useNavigate();
    const [syncDialogOpen, setSyncDialogOpen] = useState(false);
    const [syncDateTime, setSyncDateTime] = useState('');
    const [syncing, setSyncing] = useState(false);

    const handleSync = async (useCustomDate = false) => {
        try {
            setSyncing(true);
            let endpoint = '/admin/sync/trigger';
            
            if (useCustomDate && syncDateTime) {
                const dt = DateTime.fromISO(syncDateTime);
                if (dt.isValid) {
                    endpoint += `?fromDate=${dt.toISO()}`;
                }
            }
            
            await api.post(endpoint);
            alert('Sync started successfully');
        } catch (error) {
            alert('Failed to start sync');
        } finally {
            setSyncing(false);
            setSyncDialogOpen(false);
        }
    };

    const sections = [
        {
            title: "Main",
            links: [
                { icon: <PeopleIcon />, title: 'Clients', path: '/admin/clients' },
                { icon: <ChildCareIcon />, title: 'Children', path: '/admin/children' },
                { icon: <GroupIcon />, title: 'Members', path: '/admin/members' },
                { icon: <CheckCircleIcon />, title: 'Check-ins', path: '/admin/check-ins' },
                { icon: <AnalyticsIcon />, title: 'Metrics', path: '/admin/metrics' },
                { icon: <SyncIcon />, title: 'Sync', onClick: () => setSyncDialogOpen(true) },
                { icon: <HistoryIcon />, title: 'Sync History', path: '/admin/sync-history' },
                { icon: <EmailIcon />, title: 'Email Settings', path: '/admin/email-settings' }
            ]
        },
        {
            title: "Import Tools",
            links: [
                { icon: <CloudUploadIcon />, title: 'Import Clients', path: '/admin/import-clients' },
                { icon: <ChildCareIcon />, title: 'Import Children', path: '/admin/import-children' },
                { icon: <LinkIcon />, title: 'Import Relationships', path: '/admin/import-relationships' }
            ]
        },
        {
            title: "Resources",
            links: [
                { icon: <MenuBookIcon />, title: 'Admin Guide', path: '/admin/guide' },
                { icon: <PersonAddIcon />, title: 'Register Client', path: '/register' }
            ]
        }
    ];

    return (
        <Box 
            sx={{ 
                minHeight: '100vh',
                background: 'linear-gradient(135deg, #1a1a1a 0%, #0a0a0a 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                p: 3
            }}
        >
            <Grid 
                container 
                spacing={4} 
                maxWidth="lg" 
                justifyContent="center"
            >
                {sections.map((section, sectionIndex) => (
                    <Grid item xs={12} md={4} key={sectionIndex}>
                        <Paper 
                            elevation={24}
                            sx={{
                                p: 3,
                                background: 'rgba(255, 255, 255, 0.05)',
                                backdropFilter: 'blur(10px)',
                                borderRadius: 2,
                                border: '1px solid rgba(255, 255, 255, 0.1)'
                            }}
                        >
                            <Typography 
                                variant="h6" 
                                sx={{ 
                                    color: 'primary.main',
                                    mb: 3,
                                    textAlign: 'center',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase',
                                    letterSpacing: 1
                                }}
                            >
                                {section.title}
                            </Typography>
                            <Grid container spacing={2} justifyContent="center">
                                {section.links.map((link, index) => (
                                    <Grid item key={index}>
                                        <Tooltip 
                                            title={link.title} 
                                            placement="top"
                                            arrow
                                        >
                                            <IconButton
                                                onClick={link.onClick || (() => navigate(link.path))}
                                                sx={{
                                                    color: 'rgba(255, 255, 255, 0.8)',
                                                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                                                    p: 2,
                                                    '&:hover': {
                                                        color: 'primary.main',
                                                        transform: 'scale(1.1)',
                                                        bgcolor: 'rgba(255, 255, 255, 0.1)'
                                                    },
                                                    transition: 'all 0.3s ease-in-out',
                                                    fontSize: '2rem'
                                                }}
                                            >
                                                {link.icon}
                                            </IconButton>
                                        </Tooltip>
                                    </Grid>
                                ))}
                            </Grid>
                        </Paper>
                    </Grid>
                ))}
            </Grid>

            {/* Sync Dialog */}
            <Dialog 
                open={syncDialogOpen} 
                onClose={() => !syncing && setSyncDialogOpen(false)}
                PaperProps={{
                    sx: {
                        background: '#1a1a1a',
                        color: 'white',
                        minWidth: '300px'
                    }
                }}
            >
                <DialogTitle>Manual Sync</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                        Choose how you want to sync:
                    </Typography>
                    <TextField
                        label="Custom Start Date/Time"
                        type="datetime-local"
                        value={syncDateTime}
                        onChange={(e) => setSyncDateTime(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        fullWidth
                        sx={{
                            mt: 2,
                            '& .MuiOutlinedInput-root': {
                                color: 'white',
                                '& fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.23)',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'rgba(255, 255, 255, 0.5)',
                                },
                            },
                            '& .MuiInputLabel-root': {
                                color: 'rgba(255, 255, 255, 0.7)',
                            },
                        }}
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button 
                        onClick={() => !syncing && setSyncDialogOpen(false)}
                        disabled={syncing}
                        sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleSync(true)}
                        disabled={syncing || !syncDateTime}
                        variant="contained"
                        color="primary"
                    >
                        Sync from Date
                    </Button>
                    <Button
                        onClick={() => handleSync(false)}
                        disabled={syncing}
                        variant="contained"
                        color="primary"
                    >
                        Sync from Last
                    </Button>
                </DialogActions>
            </Dialog>

        </Box>
    );
};

export default Todd; 